import { useEffect, useRef, useCallback } from 'react';
import { usePageVisibility } from './core';

export type BoardEventType =
  | 'square_claimed'
  | 'square_cleared'
  | 'square_assigned'
  | 'square_labeled'
  | 'hit_recorded'
  | 'hit_updated'
  | 'hit_deleted'
  | 'board_locked'
  | 'board_unlocked'
  | 'board_published'
  | 'board_status_change'
  | 'numbers_randomized'
  | 'board_updated'
  | 'squares_reset'
  | 'players_randomized'
  | 'request_created'
  | 'request_approved'
  | 'request_denied'
  | 'request_deleted'
  | 'board_deleted'
  | 'offline_player_merged'
  | 'merge_request_created'
  | 'merge_request_approved'
  | 'merge_request_denied'
  | 'pending_score_created'
  | 'pending_score_approved'
  | 'pending_score_dismissed'
  | 'game_status_changed'
  | 'board_auto_locked';

export interface BoardEvent {
  type: BoardEventType;
  boardId: string;
  timestamp: string;
  targetUserId?: string; // If set, only this user should act on the event
  winnerIds?: string[]; // For hit events, list of winner user IDs
}

interface UseBoardEventsOptions {
  onSquareChange?: () => void;
  onHitChange?: (isWinner: boolean) => void; // isWinner indicates if current user won
  onBoardChange?: () => void;
  onRequestStatusChange?: () => void;
  onBoardDeleted?: () => void; // Called when board is deleted
  onOfflinePlayerMerged?: () => void; // Called when an offline player is merged to a user
  onMergeRequestChange?: () => void; // Called when a merge request is created/approved/denied
  onPendingScoreChange?: () => void; // Called when a pending score update is created/approved/dismissed
  onGameStatusChange?: () => void; // Called when the live game status changes
  currentUserId?: string; // Used to filter user-targeted events
}

// Minimum time (ms) tab must be hidden before triggering a full refresh on return
// This prevents unnecessary API calls for quick tab switches
const MIN_HIDDEN_DURATION_FOR_REFRESH = 5000;

export function useBoardEvents(boardId: string | undefined, options: UseBoardEventsOptions = {}) {
  const { onSquareChange, onHitChange, onBoardChange, onRequestStatusChange, onBoardDeleted, onOfflinePlayerMerged, onMergeRequestChange, onPendingScoreChange, onGameStatusChange, currentUserId } = options;
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const hiddenAtRef = useRef<number | null>(null);
  const isVisible = usePageVisibility();

  const handleEvent = useCallback(
    (event: BoardEvent) => {
      // For user-targeted events (except request status changes), only process if targeting current user
      // Request status changes are broadcast to all users so managers can update their player lists
      const isUserTargetedEvent = event.targetUserId && currentUserId && event.targetUserId !== currentUserId;
      const isRequestEvent = event.type === 'request_created' || event.type === 'request_approved' || event.type === 'request_denied' || event.type === 'request_deleted';
      if (isUserTargetedEvent && !isRequestEvent) {
        console.log('[SSE] Ignoring event for different user:', event.type, event.targetUserId);
        return;
      }

      switch (event.type) {
        case 'square_claimed':
        case 'square_cleared':
        case 'square_assigned':
        case 'square_labeled':
        case 'squares_reset':
        case 'players_randomized':
          onSquareChange?.();
          break;

        case 'hit_recorded':
        case 'hit_updated':
        case 'hit_deleted': {
          // Check if current user is in the winners list (for recorded or updated hits)
          const isWinner = (event.type === 'hit_recorded' || event.type === 'hit_updated') &&
            currentUserId !== undefined &&
            event.winnerIds?.includes(currentUserId) === true;
          onHitChange?.(isWinner);
          break;
        }

        case 'board_locked':
        case 'board_unlocked':
        case 'board_published':
        case 'board_status_change':
        case 'numbers_randomized':
        case 'board_updated':
          onBoardChange?.();
          // Numbers randomized also affects squares display
          if (event.type === 'numbers_randomized') {
            onSquareChange?.();
          }
          break;

        case 'request_created':
        case 'request_approved':
        case 'request_denied':
        case 'request_deleted':
          console.log('[SSE] Request event received, calling onRequestStatusChange:', event.type);
          onRequestStatusChange?.();
          break;

        case 'board_deleted':
          onBoardDeleted?.();
          break;

        case 'offline_player_merged':
          onOfflinePlayerMerged?.();
          // Merge affects squares and potentially ledger/balances
          onSquareChange?.();
          break;

        case 'merge_request_created':
        case 'merge_request_approved':
        case 'merge_request_denied':
          onMergeRequestChange?.();
          // If approved, also refresh squares and offline players
          if (event.type === 'merge_request_approved') {
            onOfflinePlayerMerged?.();
            onSquareChange?.();
          }
          break;

        case 'pending_score_created':
        case 'pending_score_approved':
        case 'pending_score_dismissed':
          onPendingScoreChange?.();
          // Approved scores also create hits
          if (event.type === 'pending_score_approved') {
            onHitChange?.(false);
          }
          break;

        case 'game_status_changed':
          onGameStatusChange?.();
          onBoardChange?.();
          break;

        case 'board_auto_locked':
          onBoardChange?.();
          break;
      }
    },
    [onSquareChange, onHitChange, onBoardChange, onRequestStatusChange, onBoardDeleted, onOfflinePlayerMerged, onMergeRequestChange, onPendingScoreChange, onGameStatusChange, currentUserId]
  );

  const connect = useCallback(() => {
    if (!boardId) return;

    // Use relative path - Vite proxy will handle routing
    // With HTTP-only cookies, we use withCredentials to include cookies automatically
    // The browser will send the auth cookie with the SSE request
    const url = `/api/squareup/boards/${boardId}/events`;
    const eventSource = new EventSource(url, { withCredentials: true });
    eventSourceRef.current = eventSource;

    eventSource.addEventListener('connected', () => {
      console.log('[SSE] Connected to board events');
    });

    eventSource.addEventListener('update', (e) => {
      try {
        const event: BoardEvent = JSON.parse(e.data);
        console.log('[SSE] Received event:', event.type);
        handleEvent(event);
      } catch (err) {
        console.error('[SSE] Failed to parse event:', err);
      }
    });

    eventSource.onerror = () => {
      console.log('[SSE] Connection error, will reconnect...');
      eventSource.close();
      eventSourceRef.current = null;
      // Only reconnect if tab is visible
      if (!document.hidden) {
        reconnectTimeoutRef.current = window.setTimeout(connect, 3000);
      }
    };
  }, [boardId, handleEvent]);

  const disconnect = useCallback(() => {
    if (eventSourceRef.current) {
      console.log('[SSE] Disconnecting board events (tab hidden)');
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  // Manage SSE connection based on boardId and visibility
  // Consolidated into single useEffect to prevent race conditions
  useEffect(() => {
    if (!boardId) {
      disconnect();
      return;
    }

    if (isVisible) {
      // Tab is visible - connect if not already connected
      if (!eventSourceRef.current) {
        connect();
        // Only refresh if tab was hidden for a meaningful duration
        // This prevents unnecessary API calls for quick tab switches
        const wasHiddenLongEnough = hiddenAtRef.current !== null &&
          Date.now() - hiddenAtRef.current >= MIN_HIDDEN_DURATION_FOR_REFRESH;
        if (wasHiddenLongEnough) {
          console.log('[SSE] Tab visible again after', Date.now() - hiddenAtRef.current!, 'ms, refreshing data...');
          onSquareChange?.();
          onBoardChange?.();
          onHitChange?.(false);
          onRequestStatusChange?.();
          onOfflinePlayerMerged?.();
          onMergeRequestChange?.();
          onPendingScoreChange?.();
          onGameStatusChange?.();
        }
      }
      hiddenAtRef.current = null;
    } else {
      // Tab is hidden - record timestamp and disconnect to save resources
      if (hiddenAtRef.current === null) {
        hiddenAtRef.current = Date.now();
      }
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [boardId, isVisible, connect, disconnect, onSquareChange, onBoardChange, onHitChange, onRequestStatusChange, onOfflinePlayerMerged, onMergeRequestChange, onPendingScoreChange, onGameStatusChange]);
}
